<template>
  <div class="animated fadeIn">

    <!-- Main area -->
    <b-card>

      <template v-if="teamGroups.length > 0">
        <h3>My Team's User Groups</h3>

        <b-row>
          <template v-for="group in teamGroups">
            
            <b-col md="6">
              <b-card class="card-accent-success" :ref="'group' + group.id.toString()">
                <div slot="header">
                  {{ group.name }} - {{ group.description }}
                </div>
                <b-card-body>
                  <b-row>
                  <b-col md="10">
                    <v-select 
                      label="real_name" 
                      :options="teamMembers"
                      :value="groupMembers(group.id)" 
                      :multiple="true"
                      :clearable="true"
                      :searchable="true"
                      :close-on-select="false"
                      @input="teammatesSelected($event, group.id)"
                      :ref="'members' + group.id.toString()"
                    ></v-select>
                  </b-col>

                  <b-col md="2">
                    <b-button 
                      block 
                      variant="primary"
                      :ref="'save' + group.id.toString()"
                      @click="updateExistingGroup(group.id)">
                        Save 
                    </b-button>
                    <b-button 
                      block 
                      variant="danger"
                      :ref="'delete' + group.id.toString()"
                      @click="promptDelete(group.id)">
                        Delete 
                    </b-button>
                  </b-col>
                  </b-row>
                </b-card-body>

              </b-card>
            </b-col>

          </template>
        </b-row>

      </template>
      <template v-else>
        <h3>You don't have any user groups yet, time to create some!</h3>

        <b-row>
          <span class="bg-info text-white p-1 ml-4 mt-4 rounded">
            &nbsp;&nbsp;<i class="far fa-lightbulb mr-1"></i>
            User groups allow you to filter dashboard views more efficiently by not having to select individual sets of users each time.
          </span>
        </b-row>

        <b-row>
          <span class="bg-info text-white p-1 ml-4 mt-2 mb-4 rounded">
            &nbsp;&nbsp;<i class="far fa-lightbulb mr-1"></i>
            If you find yourself regularly querying on the same set of users, consider creating a group! 
          </span>
        </b-row>
      </template>
      <hr />

      <!-- New Group -->
      <b-button variant="primary" v-b-toggle.newGroupPanel>
        <i class="fa fa-plus-circle"></i>&nbsp;&nbsp;&nbsp;Create New Group
      </b-button>

      <b-row>
        <b-col md="5">
      <b-collapse id="newGroupPanel">
        <b-card>
          <b-form class="needs-validation" id="newGroupForm">
            <b-form-group>
              <b-input-group>
                <b-input-group-prepend>
                  <b-input-group-text><i class="fa fa-users"></i></b-input-group-text>
                </b-input-group-prepend>
                <b-form-input type="text" placeholder="Group name" ref="newName" v-model="newGroupName" v-on:blur="newGroupNameBlurred = true" required></b-form-input>
              </b-input-group>
              <div class="invalid-feedback">Group name must be between 3 and 64 characters</div>
            </b-form-group>
            <b-form-group>
              <b-input-group>
                <b-input-group-prepend>
                  <b-input-group-text><i class="far fa-file-alt"></i></b-input-group-text>
                </b-input-group-prepend>
                <b-form-input type="text" placeholder="Description" ref="newDesc" v-model="newGroupDesc" v-on:blur="newGroupDescBlurred = true"></b-form-input>
              </b-input-group>
              <div class="invalid-feedback">Group description must be less than 255 characters</div>
            </b-form-group>
            <b-form-group>
              <b-input-group>
                <v-select 
                    label="real_name" 
                    :options="teamMembers"
                    :multiple="true"
                    :clearable="true"
                    :searchable="true"
                    :close-on-select="false"
                    @input="newGroupTeammatesSelected"
                    ref="newMembers"
                    required
                  ></v-select>
                <div class="invalid-feedback">Group must contain at least 2 members!</div>
              </b-input-group>
            </b-form-group>

            <div v-if="newGroupFormInvalid" class="alert alert-danger" role="alert">
              User Groups must have a name and at least one member!
            </div>

            <div slot="footer">
              <b-button type="reset" size="sm" variant="danger"><i class="fas fa-ban"></i> Reset</b-button> &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
              <b-button type="submit" size="sm" variant="success" @click.prevent="submitNewGroup"><i class="far fa-dot-circle"></i> Submit</b-button> 
              
            </div>
          </b-form>

        </b-card>
      </b-collapse>

      </b-col>
      </b-row>
      
    </b-card>

    <!-- Group Deletion Confirmation Modal -->
    <b-modal title="Confirm Group Deletion" class="modal-danger" v-model="deleteModal" @ok="deleteGroup" ok-variant="danger">
      Are you sure you want to delete this group?
    </b-modal>

  </div>
</template>



<script>
import { mapGetters, mapActions } from 'vuex'
import moment from 'moment'
import { getStyle } from '@coreui/coreui-pro/dist/js/coreui-utilities'

// ridiculous that js does not have anything like this builtin
const isEqual = (a, b) => {
  if (a === b) return true;
  if (a instanceof Date && b instanceof Date) return a.getTime() === b.getTime();
  if (!a || !b || (typeof a !== 'object' && typeof b !== 'object')) return a === b;
  if (a === null || a === undefined || b === null || b === undefined) return false;
  if (a.prototype !== b.prototype) return false;
  let keys = Object.keys(a);
  if (keys.length !== Object.keys(b).length) return false;
  return keys.every(k => isEqual(a[k], b[k]));
};

export default {
  name: 'UserGroupsDashboard',
  components: {
  },
  data: function () {
    return {
      pageName: 'usergroups',
      groupMememberMap: {},
      deleteModal: false,
      groupToDelete: null,

      newGroupName: '',
      newGroupDesc: '',
      newGroupMembers: [],
      newGroupNameBlurred: false,
      newGroupDescBlurred: false,

      newGroupFormInvalid: false,

      successColor: getStyle('--success'),
      dangerColor: getStyle('--danger')
    }
    
  },

  computed: {
    teamMembers() {
      return this.$store.getters.teamMembers
    },
    teamGroups() {
      return this.$store.getters.teamGroups
    }
  },
  
  methods: {
    groupMembers(groupId) {
      let g = this.$store.getters.teamGroup(groupId)
      // console.log(g)
      let members = []

      for(const uid of g.members) {
        let member = this.$store.getters.teamMember(uid)
        members.push(member)
      }
      // console.log(members)
      return members
    },
    groupModified(groupId) {
      // compare the value of the select against the team metadata.
      // successfully saving a group will update the team metadata
      let og = this.groupMembers(groupId)
      let ng = this.groupMememberMap[groupId]
      let equal = isEqual(og, ng)
      console.log('group is equal: ' + equal.toString())
      return isEqual(equal)
    },
    newGroupTeammatesSelected(value) {
      this.newGroupMembers = value
    },
    teammatesSelected(value, groupId) {
      this.groupMememberMap[groupId] = value

      // toggle save button
      let btn = this.$refs['save' + groupId.toString()][0]
      // console.log(btn)
      btn.hidden = isEqual(this.groupMememberMap[groupId], this.groupMembers(groupId))
    },

    updateExistingGroup(groupId) {
      console.log('update group ' + groupId)
      let g = this.$store.getters.teamGroup(groupId)
      let updatedMembers = this.groupMememberMap[groupId]
      // console.log(updatedMembers)

      let name = g.name
      let desc = g.description
      let uids = updatedMembers.map(member => member.id)

      this.$store.dispatch('updateUserGroup', [groupId, name, desc, uids])
    },

    validateNewGroup() {
      let name = this.$refs.newName
      let desc = this.$refs.newDesc
      let members = this.$refs.newMembers

      // let formId = 'newGroupForm'
      // let nodes = document.querySelectorAll(`#${formId} :invalid`)
      // console.log(nodes)
      // if(nodes.length == 0) {
      //   return true
      // }
      // return false
      if(this.newGroupName.trim().length == 0) {
        return false
      }

      if(this.newGroupDesc.length >= 254) {
        return false
      }

      if(this.newGroupMembers.length == 0) {
        return false
      }

      return true
    },
    submitNewGroup(event) {
      // console.log('submit!')
      // console.log(this.newGroupName + ', ' + this.newGroupDesc)
      // console.log(this.newGroupMembers)
      this.newGroupFormInvalid = !this.validateNewGroup()
      if(this.newGroupFormInvalid) {
        return
      }

      let name = this.newGroupName
      let desc = (this.newGroupDesc.length > 0 ? this.newGroupDesc : null)
      let uids = this.newGroupMembers.map(member => member.id)
      // console.log(uids)

      this.$store.dispatch('createNewUserGroup', [name, desc, uids])

      this.$refs.newName.text = ''
      this.$refs.newDesc.text = ''
      this.$refs.newMembers.value = []  // this produces a warning.  better way?
      this.newGroupName = ''
      this.newGroupDesc = ''
      this.newGroupMembers = []
      this.newGroupFormInvalid = false
    },
    promptDelete(groupId) {
      this.groupToDelete = groupId
      this.deleteModal = true
    },
    deleteGroup() {
      console.log('deleting group: ' + this.groupToDelete)
      this.deleteModal = false
      this.$store.dispatch('deleteUserGroup', this.groupToDelete)
      this.groupToDelete = null
    }
  },

  mounted() {
    this.$store.commit('currentPage', this.pageName)
  }
}
</script>
